import fiftyFiftyImage1 from '@public/images/shipsticks/fifty-fifty-1.png'
import fiftyFiftyImage3 from '@public/images/shipsticks/fifty-fifty-3.jpg'
import fiftyFiftyImage4 from '@public/images/shipsticks/fifty-fifty-4.jpg'
import fiftyFiftyImage5 from '@public/images/shipsticks/fifty-fifty-5.jpg'
import React from 'react'

import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { TestimonialsProps } from 'lib/components/Testimonials/Testimonials'

import { BrandEmphasis } from '@/components/shipplay/ui/BrandEmphasis'
import type { AutoScrollerImage } from '@/components/shipplay/ui_patterns/AutoScroller'
import { StructuredData } from '@/utils/brand'

export interface ShipplayCta {
  text: string
  to: string
}

const shipplayLogoUrl =
  'https://s3.amazonaws.com/shipsticks_partner_assets_production/shipplay/' +
  'S_P_Blue_Ship_Full_Color_RGB_CROPPED.svg'

const structuredData: StructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.shipplay.com/#organization',
      name: 'Ship&Play',
      url: 'https://www.shipplay.com/',
      sameAs: [
        'https://www.facebook.com/ShipAndPlay',
        'https://www.instagram.com/shipandplayofficial/',
        'https://linkedin.com/company/ship-play',
        'https://www.youtube.com/@shipandplay',
        'https://x.com/shipandplay_',
        'https://www.tiktok.com/@shipandplay',
      ],
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://www.shipplay.com/#logo',
        url: shipplayLogoUrl,
        caption: 'Ship&Play',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '11400 N. Jog Road, Suite 300',
        addressLocality: 'Palm Beach Gardens',
        addressRegion: 'Florida',
        postalCode: '33418',
        addressCountry: 'US',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-855-792-7529',
        email: 'info@shipplay.com',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://www.shipplay.com/#website',
      url: 'https://www.shipplay.com/',
      name: 'Ship&Play',
      description: 'Door-to-Door Luggage & Travel Gear Shipping',
      publisher: {
        '@id': 'https://www.shipplay.com/#organization',
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.shipplay.com/blog/?s={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'ImageObject',
      '@id': 'https://www.shipplay.com/#primaryimage',
      url: shipplayLogoUrl,
    },
    {
      '@type': 'WebPage',
      '@id': 'https://www.shipplay.com/#webpage',
      url: 'https://www.shipplay.com/',
      name: 'Ship&Play: Ship Your Luggage & Gear Door-to-Door',
      inLanguage: 'en-US',
      isPartOf: {
        '@id': 'https://www.shipplay.com/#website',
      },
      about: {
        '@id': 'https://www.shipplay.com/#organization',
      },
      primaryImageOfPage: {
        '@id': 'https://www.shipplay.com/#primaryimage',
      },
      description:
        'Ship your luggage, golf clubs, skis, and gear ahead with Ship&Play and travel ' +
        'completely hands-free. Pack everything, carry nothing.',
    },
    {
      '@type': 'Product',
      '@id': 'https://www.shipplay.com/#product',
      url: 'https://www.shipplay.com/',
      name: 'Luggage & Travel Gear Shipping by Ship&Play',
      description:
        'Ship your luggage, golf clubs, skis, and gear ahead with Ship&Play and travel ' +
        'completely hands-free. Pack everything, carry nothing.',
      image: shipplayLogoUrl,
      brand: {
        '@type': 'Organization',
        '@id': 'https://www.shipplay.com/#organization',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 4.7,
        reviewCount: 42390,
        bestRating: 5,
      },
    },
  ],
} as const

export interface VideoMeta {
  header?: string | React.ReactNode
  videoUrl: string
  caption?: string
  body?: string
  button?: {
    text: string
    link: {
      href: string
    }
  }
}

export interface HomePageContent {
  blogMeta: {
    buttonText: string
    subheader: string
  }
  heroMeta: {
    header: React.ReactNode
    subheader: string
    image: string
    stepsHeader: React.ReactNode
    steps: {
      header: string
      subheader: string
    }[]
  }
  fiftyFiftiesMeta: FiftyFiftyProps[]
  videoMeta?: VideoMeta
  testimonialsMeta: TestimonialsProps
  structuredData: typeof structuredData
}

const blogMeta = {
  buttonText: 'Continue reading',
  subheader:
    'Discover new destinations, smarter ways to pack, and travel tips ' +
    'for getting there with nothing but a carry-on smile.',
}

const heroMeta = {
  header: <>Pack everything. Carry nothing.</>,
  subheader: 'Let us ship your luggage so you can travel freely.',
  image: '/images/shipsticks/hero-background.jpg',
  stepsHeader: (
    <>
      Travel hands-free in four <b>easy steps.</b>
    </>
  ),
  steps: [
    {
      header: 'Book',
      subheader:
        'Schedule your shipment online, in the app, or over the phone - ' +
        'whatever works best for you.',
    },
    {
      header: 'Ship',
      subheader: `
          Pack your bags and boxes as you would for any trip, attach your label,
          and we'll take it from there.
        `,
    },
    {
      header: 'Travel',
      subheader: `
          Sashay through the airport like a carefree jet setter -
          no lines, no lugging, no baggage claim.
        `,
    },
    {
      header: 'Play',
      subheader: `
          Arrive at your destination to find everything already there, waiting before you are.
        `,
    },
  ],
}

const fiftyFiftiesMeta: FiftyFiftyProps[] = [
  {
    header: (
      <>
        No baggage, <strong>no boundaries.</strong>
      </>
    ),
    body: `
      Imagine a trip where getting there and back is the easy part - no lugging, no lines,
      and no "your bag didn't make it on the plane". Just travel bliss without the baggage,
      so you're free to explore hidden gems and wander off the beaten path.
    `,
    image: {
      src: fiftyFiftyImage1,
    },
    button: {
      text: 'Get started',
      link: {
        href: '/book/ship',
      },
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        See <strong>how simple </strong>it is to ship.
      </>
    ),
    body: `
      Just pack your bags, suitcases, and boxes as you would for any trip, and we’ll pick
      them up from your home or office. Or, you can drop them off before you head out.
    `,
    videoUrl: 'https://www.youtube.com/watch?v=fuvtY9vlfGs',
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Travel, <strong>transformed.</strong>
      </>
    ),
    body: `
      Skip the wait at the airport, the excess baggage fees, the upsized rental to fit your gear,
      and the back-breaking schlep through the terminal. Arrive relaxed and ready for whatever
      your trip has in store.
    `,
    image: {
      src: fiftyFiftyImage3,
    },
    button: {
      text: 'Try it out',
      link: {
        href: '/book/ship',
      },
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        We treat your bags <strong>like our own.</strong>
      </>
    ),
    body: `
      With real-time tracking, complimentary insurance, and dedicated customer service,
       your luggage and gear get the royal treatment - something you don’t see on the tarmac.
    `,
    image: {
      src: fiftyFiftyImage4,
    },
    button: {
      text: 'What we offer',
      link: {
        href: '/guarantee',
      },
    },
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Your trip, <strong>your way.</strong>
      </>
    ),
    body: `
      We ship worldwide and operate on your schedule. Whether it's a suitcase waiting at the
      resort, skis sent ahead to the slopes, or clubs delivered to the course and back home
      again, we’ve got you covered.
    `,
    image: {
      src: fiftyFiftyImage5,
    },
    button: {
      text: 'Discover more',
      link: {
        href: 'https://www.shipplay.com/blog/travel/',
      },
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
]

const videoMeta: VideoMeta = {
  header: 'How Ship&Play works',
  videoUrl: 'https://www.youtube.com/watch?v=puTgjmds_Gs',
  caption: 'We ship, you play',
  body: `
    Ship your luggage, golf clubs, skis, bikes, or family gear ahead of time and skip
    the hassle of traveling with them. We'll pick up your bags or
    guide you to a drop-off option that fits your schedule. Everything
    arrives safely at your destination so you can travel completely hands-free.
  `,
  button: {
    text: 'Watch now',
    link: {
      href: 'https://www.youtube.com/watch?v=puTgjmds_Gs',
    },
  },
}

const testimonialsMeta: TestimonialsProps = {
  title: (
    <>
      What our customers <strong>have&nbsp;to&nbsp;say</strong>
    </>
  ),
  testimonials: [
    {
      stars: 5,
      quote:
        'It was GREAT to not drag our suitcases through airports, rental cars, and hotels. ' +
        'We landed and our bags were already at the front desk.',
      attribution: {
        icon: 'luggage',
        name: 'Bob',
      },
    },
    {
      stars: 5,
      quote:
        "Couldn't have been easier - everything got there and back on time, no damage, perfect.",
      attribution: {
        icon: 'suitcase',
        name: 'Tom',
      },
    },
    {
      stars: 5,
      quote: 'This service was great! Very easy, timely, and tracked every step of the way.',
      attribution: {
        icon: 'travel',
        name: 'Sean',
      },
    },
    {
      stars: 4,
      quote:
        'Great customer service…they responded to my questions quickly, even over the weekend.',
      attribution: {
        icon: 'globe',
        name: 'Vincent',
      },
    },
    {
      stars: 5,
      quote:
        'I shipped my skis ahead to the mountain and my clubs home from the course - ' +
        'each time Ship&Play exceeded my expectations.',
      attribution: {
        icon: 'skiresort',
        name: 'Greg',
      },
    },
    {
      stars: 4,
      quote:
        'Convenient and easy. Traveling with the kids is so much simpler when the stroller ' +
        'and bags are waiting for us.',
      attribution: {
        icon: 'golf',
        name: 'Ken',
      },
    },
  ],
}

const content: HomePageContent = {
  blogMeta,
  heroMeta,
  fiftyFiftiesMeta,
  videoMeta,
  testimonialsMeta,
  structuredData,
}

// ---------------------------------------------------------------------------
// Ship&Play home page — swappable copy for the home-only shared sections
// (Intro, MonthlyStats, StepsToShip, FeatureWeightlessTravel,
// FeaturePeaceOfMind, CustomerReviews). Each section component reads a single
// `content: XxxContent` prop from ShipplayHomePage so the copy is portable
// across brands without touching JSX.
// ---------------------------------------------------------------------------

export interface ShipplayIntroImage {
  src: string
  alt: string
  className: string
}

export interface ShipplayIntroContent {
  /** Full heading; wrap emphasized fragments in `<BrandEmphasis>`. */
  title: React.ReactNode
  body: React.ReactNode
  tagline: string
  leftImage: ShipplayIntroImage
  rightImage: ShipplayIntroImage
  cta?: ShipplayCta
}

export interface ShipplayMonthlyStatsCard {
  stat: string
  label: string
  /**
   * Reference key for the icon rendered inside the card. The page component
   * maps this key to an actual icon node so the copy stays JSX-free.
   */
  icon: 'luggage-tag' | 'map' | 'ranking' | 'dancing'
}

export interface ShipplayMonthlyStatsContent {
  /** Full heading; wrap emphasized fragments in `<BrandEmphasis>`. */
  heading: React.ReactNode
  cards: ShipplayMonthlyStatsCard[]
}

export interface ShipplayStepsToShipCard {
  step: string
  title: React.ReactNode
  description: React.ReactNode
  icon: 'calendar' | 'luggage-tag' | 'shipping-truck' | 'airplane'
}

export interface ShipplayStepsToShipContent {
  /** Full heading; wrap emphasized fragments in `<BrandEmphasis>`. */
  heading: React.ReactNode
  subheading?: React.ReactNode
  cards: ShipplayStepsToShipCard[]
  cta: {
    text: string
    to: string
  }
}

export interface ShipplayFeatureListItemContent {
  title: string
  body: React.ReactNode
}

export interface ShipplayFeatureWeightlessTravelContent {
  imgUrl: string
  imgAlt: string
  companion: {
    id: number
    className: string
  }
  /** Full headline as JSX — preserves the structural `<br />` and inline color spans. */
  heading: React.ReactNode
  items: ShipplayFeatureListItemContent[]
  cta?: ShipplayCta
}

export interface ShipplayFeaturePeaceOfMindContent {
  imgUrl: string
  imgAlt: string
  companion: {
    id: number
    className: string
  }
  /** Full headline as JSX — preserves the forced 3-line mobile wraps and inline color spans. */
  heading: React.ReactNode
  items: ShipplayFeatureListItemContent[]
  cta?: ShipplayCta
}

export interface ShipplayVideoReview {
  imgUrl: string
  videoUrl: string
  profileUrl: string
  author: string
}

export interface ShipplayTextReview {
  author: string
  quote: string
  stars?: number
  isVerified?: boolean
}

export interface ShipplayCustomerReviewsContent {
  /** Full heading; wrap emphasized fragments in `<BrandEmphasis>`. */
  heading: React.ReactNode
  videoReviews: ShipplayVideoReview[]
  textReviews: ShipplayTextReview[]
}

export interface ShipplayBlogCardsContent {
  /** Full heading; wrap emphasized fragments in `<BrandEmphasis>`. */
  heading: React.ReactNode
  description: React.ReactNode
}

export interface ShipplayOfficialPartnersContent {
  /** Full title; wrap emphasized fragments in `<BrandEmphasis>`. */
  title: React.ReactNode
  logos: AutoScrollerImage[]
}

export interface ShipplayHomeContent {
  officialPartners: ShipplayOfficialPartnersContent
  intro: ShipplayIntroContent
  monthlyStats: ShipplayMonthlyStatsContent
  stepsToShip: ShipplayStepsToShipContent
  featureWeightlessTravel: ShipplayFeatureWeightlessTravelContent
  featurePeaceOfMind: ShipplayFeaturePeaceOfMindContent
  customerReviews: ShipplayCustomerReviewsContent
  blogCards: ShipplayBlogCardsContent
}

const CUSTOMER_REVIEWS_S3_ROOT = 'https://s3.amazonaws.com/shipsticks_partner_assets_production'
const SHIPANDPLAY_S3_BASE = `${CUSTOMER_REVIEWS_S3_ROOT}/69b1ea22def334404ebf7658`
const TRAVELWITHLIVII_S3_BASE = `${CUSTOMER_REVIEWS_S3_ROOT}/69b1eaa55312dd51426b6cdd`
const CARAANNEJONES_S3_BASE = `${CUSTOMER_REVIEWS_S3_ROOT}/69b1eaa65312dd51426b6cde`
const PORTHOLECRUISE_S3_BASE = `${CUSTOMER_REVIEWS_S3_ROOT}/69b1eaa75312dd51426b6cdf`
const SHIPANDPLAY_AVATAR = `${SHIPANDPLAY_S3_BASE}/images/SnP-avatar.png`

export const shipplayContent: ShipplayHomeContent = {
  officialPartners: {
    title: (
      <>
        Official <BrandEmphasis>Partners</BrandEmphasis>
      </>
    ),
    logos: [
      { imgUrl: '/images/shipplay/logos/virtuoso.webp', imgAlt: 'Virtuoso' },
      { imgUrl: '/images/shipplay/logos/signature-travel-network.webp', imgAlt: 'Signature' },
      { imgUrl: '/images/shipplay/logos/ikon-pass.webp', imgAlt: 'Ikon Pass' },
      { imgUrl: '/images/shipplay/logos/azamara-cruises.webp', imgAlt: 'Azamara Cruises' },
      { imgUrl: '/images/shipplay/logos/clear.webp', imgAlt: 'Clear' },
      { imgUrl: '/images/shipplay/logos/montage.webp', imgAlt: 'Montage' },
      { imgUrl: '/images/shipplay/logos/veer.svg', imgAlt: 'Veer' },
      { imgUrl: '/images/shipplay/logos/pga-of-america.webp', imgAlt: 'PGA of America' },
    ],
  },
  intro: {
    title: (
      <>
        We Ship. <BrandEmphasis>You Play.</BrandEmphasis>
      </>
    ),
    body: (
      <>
        Luggage, family essentials, sports equipment, and everything in between&mdash;send it ahead,
        ship it home, or both. No more hauling bags through crowded airports. No more crossing your
        fingers at baggage claim, wondering if your belongings made the connection.
      </>
    ),
    tagline: 'You’ll wonder how you ever did it any other way.',
    cta: {
      text: 'What Can I Ship?',
      to: '/what-can-i-ship',
    },
    leftImage: {
      src: '/images/shipplay/companions/luggage.webp',
      alt: 'Ship luggage',
      className:
        'snplay:absolute snplay:left-6 snplay:max-w-[45%] snplay:max-md:bottom-20' +
        ' snplay:md:top-1/2 snplay:md:max-w-[20%] snplay:md:-translate-y-1/2' +
        ' snplay:lg:left-16 snplay:xl:left-32',
    },
    rightImage: {
      src: '/images/shipplay/companions/equipment.webp',
      alt: 'Ship sports equipment',
      className:
        'snplay:absolute snplay:right-6 snplay:max-w-[45%] snplay:max-md:bottom-20' +
        ' snplay:md:top-1/2 snplay:md:max-w-[23%] snplay:md:-translate-y-1/2' +
        ' snplay:lg:right-16 snplay:xl:right-32',
    },
  },
  monthlyStats: {
    heading: (
      <>
        Trusted by <BrandEmphasis>Travelers Everywhere</BrandEmphasis>
      </>
    ),
    cards: [
      { stat: '5,120,143', label: 'Bags shipped', icon: 'luggage-tag' },
      { stat: '100+', label: 'Countries served', icon: 'map' },
      { stat: '4.7/5', label: 'Customer Rating', icon: 'ranking' },
      { stat: '1,014,276', label: 'Satisfied travelers', icon: 'dancing' },
    ],
  },
  stepsToShip: {
    heading: (
      <>
        Ship Your Luggage in <BrandEmphasis>Four Easy Steps</BrandEmphasis>
      </>
    ),
    subheading: 'To your destination, back home, or both.',
    cards: [
      {
        step: '1',
        title: 'Book',
        description:
          'Easily book online or over the phone. Choose your dates and destination to get started.',
        icon: 'calendar',
      },
      {
        step: '2',
        title: 'Label',
        description:
          'Pack your bags as you would for airline travel and attach your shipping label.',
        icon: 'luggage-tag',
      },
      {
        step: '3',
        title: 'Ship',
        description:
          'We’ll pick up your luggage, or you can drop it off at a nearby partner location.',
        icon: 'shipping-truck',
      },
      {
        step: '4',
        title: 'Play',
        description:
          'Hop off the plane, skip baggage claim, and meet your bags at your destination.',
        icon: 'airplane',
      },
    ],
    cta: {
      text: 'Learn More',
      to: '/how-it-works',
    },
  },
  featureWeightlessTravel: {
    imgUrl: '/images/shipplay/travel-without-weight.webp',
    imgAlt: 'Travel without the weight',
    companion: {
      id: 2,
      className:
        'snplay:-bottom-14 snplay:max-w-[7.5rem] snplay:max-md:right-2' +
        ' snplay:md:-bottom-[6.5rem] snplay:md:-left-18 snplay:md:max-w-[15rem]',
    },
    heading: (
      <>
        Travel Was Always Meant to Be
        <br />
        <BrandEmphasis>This Easy</BrandEmphasis>
      </>
    ),
    items: [
      {
        title: 'Move Freely',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            Travel lighter from the moment you leave home. With your luggage shipped ahead, you’re
            free to skip the airport hassle and enjoy the journey.
          </p>
        ),
      },
      {
        title: 'Customer Support',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            From your first question to final delivery, our dedicated support team is here to help
            you, every day of the week. It’s real support from real people.
          </p>
        ),
      },
      {
        title: 'Peace of Mind',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            Every shipment includes complimentary insurance, real-time tracking, and an on-time
            delivery guarantee—allowing you to travel with confidence.
          </p>
        ),
      },
    ],
    cta: {
      text: 'Our Guarantee',
      to: '/guarantee',
    },
  },
  featurePeaceOfMind: {
    imgUrl: '/images/shipplay/peace-of-mind.webp',
    imgAlt: 'Peace of mind from departure to destination',
    companion: {
      id: 1,
      className:
        'snplay:right-2 snplay:-bottom-14 snplay:max-w-[7.5rem]' +
        ' snplay:md:-right-18 snplay:md:-bottom-[6.5rem] snplay:md:max-w-[17rem]',
    },
    heading: (
      <>
        Here for <BrandEmphasis>Wherever</BrandEmphasis>
        <br />
        <BrandEmphasis>Life Takes You</BrandEmphasis>
      </>
    ),
    items: [
      {
        title: 'Any Occasion',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            Whether it’s a one-way, round-trip, or multi-stop journey, your bags move on your
            schedule. Send your luggage, boxes, skis, golf clubs, and more.
          </p>
        ),
      },
      {
        title: 'Cruise Travel',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            Let us ship your luggage right to your stateroom or to your pre-cruise hotel. Then,
            after the final sunset, we’ll send your bags back, souvenirs and all.
          </p>
        ),
      },
      {
        title: 'Long-Term Stays',
        body: (
          <p className="snplay:text-base snplay:text-ui-secondary">
            International trips, relocations, and extended stays call for more than a carry-on. Send
            everything ahead without the airline fees or airport struggle.
          </p>
        ),
      },
    ],
    cta: {
      text: 'Get Started',
      to: '/book/ship',
    },
  },
  customerReviews: {
    heading: (
      <>
        What <BrandEmphasis>Real Users</BrandEmphasis> Say
      </>
    ),
    videoReviews: [
      // Playground uses two images per card: a small `thumb*.png` in the
      // author-label gradient bar (profileUrl) and a large `video*.png` as
      // the card's main image (imgUrl). shipandplayofficial has its own
      // pair (videoframe_10975.png + SnP-avatar.png); travelwithlivii
      // reuses `video2.png` for both slots.
      {
        imgUrl: `${SHIPANDPLAY_S3_BASE}/images/videoframe_10975.png`,
        videoUrl: `${SHIPANDPLAY_S3_BASE}/videos/hunter_1.mp4`,
        profileUrl: SHIPANDPLAY_AVATAR,
        author: 'shipandplayofficial',
      },
      {
        imgUrl: `${TRAVELWITHLIVII_S3_BASE}/images/video2.png`,
        videoUrl: `${TRAVELWITHLIVII_S3_BASE}/videos/video2.mp4`,
        profileUrl: `${TRAVELWITHLIVII_S3_BASE}/images/video2.png`,
        author: 'travelwithlivii',
      },
      {
        imgUrl: `${CARAANNEJONES_S3_BASE}/images/video3.png`,
        videoUrl: `${CARAANNEJONES_S3_BASE}/videos/video3.mp4`,
        profileUrl: `${CARAANNEJONES_S3_BASE}/images/thumb3.png`,
        author: 'cara.annejones',
      },
      {
        imgUrl: `${PORTHOLECRUISE_S3_BASE}/images/video4.png`,
        videoUrl: `${PORTHOLECRUISE_S3_BASE}/videos/video4.mp4`,
        profileUrl: `${PORTHOLECRUISE_S3_BASE}/images/thumb4.png`,
        author: 'portholecruise',
      },
    ],
    textReviews: [
      {
        author: 'Janet',
        quote:
          'Traveling with a toddler means bringing so much extra gear — Ship&Play made it so' +
          ' much easier.',
      },
      {
        author: 'John',
        quote:
          'The pickup of my suitcase at the cruise port was flawless… The booking process was' +
          ' easy, and when I called with questions, the operator was friendly and very helpful.',
      },
      {
        author: 'Reginald',
        quote:
          'The online booking was easy even for this 68-year-old! It’s cheaper than airline' +
          ' baggage fees and extremely convenient.',
      },
      {
        author: 'Dina',
        quote:
          'Sending 50 lbs of my stuff ahead to my destination — best travel experience I’ve' +
          ' ever had, hands down.',
      },
      {
        author: 'Jay',
        quote:
          'Shipment arrived from the USA to Canada in 24 hours — 3 days earlier than expected.' +
          ' Easy to schedule and pay online.',
      },
      {
        author: 'Patrick',
        quote:
          'Timeliness was outstanding, and pickup and drop-off couldn’t have been easier... I' +
          ' will absolutely use them again.',
      },
    ],
  },
  blogCards: {
    heading: (
      <>
        Find Your Next <BrandEmphasis>Adventure</BrandEmphasis>
      </>
    ),
    description:
      'From trending destinations to insider tips, explore our blog to see what’s new and next' +
      ' in travel.',
  },
}

export default content
